/**
 * File: apps/qbench-cp/reactapp/src/shared/components/AccessControl.js
 *
 * Purpose: Conditionally renders children based on an RBAC access-control check.
 *
 * Scope: Shared access-control gating component used across the customer portal to show or
 *     hide UI based on privileges.
 *
 * Tags: customer-portal, portal-roles
 *
 * Overview: Wraps the useAccessControl hook to decide whether to render its children, accepting
 *     the same options object as the hook (privilege list, `all` flag for requiring every
 *     privilege). Throws an error if no children are provided, since the component has nothing
 *     to gate otherwise. Used declaratively to hide menu items, buttons, and other UI elements
 *     from users without the required privilege.
 *
 * Exports: default export `AccessControl` component.
 */

import useAccessControl from 'shared/hooks/misc/useAccessControl';

/**
 * @typedef {Object} Props
 * @prop {React.ReactElement|null} [children]
 */
/**
 * Show or hide its `children` components via the useAccessControl() hook.
 * Uses the same options object as the hook.
 *
 * ```js
 *
 * // Checks for either A or B.
 * <AccessControl privilege={['A', 'B']}>
 *  {children}
 * </AccessControl>
 *
 * // Checks for both A and B.
 * <AccessControl all privilege={['A', 'B']}>
 *  {children}
 * </AccessControl>
 * ```
 * @param {import('shared/hooks/misc/useAccessControl').AccessControl & Props} props
 */
function AccessControl(props) {
  const { children, ...options } = props;
  if (!children)
    throw new Error(
      'You must provide a `children` component to <AccessControl> to show/hide.',
    );
  const isAllowed = useAccessControl(options);
  return isAllowed ? children : null;
}

export default AccessControl;
